import { useState } from 'react';

/**
 * Runs `onChange` during render whenever `value` differs (by `Object.is`) from the value
 * seen on the previous render. This is React's documented "adjust state while rendering"
 * pattern (react.dev → You Might Not Need an Effect → Adjusting some state when a prop
 * changes), packaged so call sites don't each hand-roll the previous-value state and guard.
 *
 * `onChange` may call setState for the calling component: React re-runs the render with the
 * new state before committing, so children never observe a stale frame and no extra commit
 * is queued the way an effect would. It does not run on the first render, and it is not a
 * place for side effects that reach outside the component — use an effect for those.
 */
export function useOnValueChange<T>(value: T, onChange: (next: T, prev: T) => void): void {
  // Both calls wrap `value` in a lambda: React runs a bare function passed to useState as a
  // lazy initializer and one passed to the setter as an updater, so a function-typed T would
  // be invoked instead of stored.
  const [prev, setPrev] = useState(() => value);
  if (!Object.is(prev, value)) {
    setPrev(() => value);
    onChange(value, prev);
  }
}
